import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const http = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });

// Token scoping:
//   /mock/*   → mock_token if present; else admin_token (admin bypass — backend accepts it)
//   /admin/*  → admin_token always
//   other     → admin_token
// IMPORTANT: startsWith("/mock/") not includes("/mock/") so that /admin/mock/* never
// picks up the mock_token and gets a 401.
http.interceptors.request.use((config) => {
  const url        = config.url || "";
  const adminToken = localStorage.getItem("proctorly_admin_token");
  const mockToken  = localStorage.getItem("mock_token");
  if (url.startsWith("/mock/")) {
    // Prefer mock_token; fall back to admin_token so admins don't need OTP login
    const token = mockToken || adminToken;
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
  } else if (adminToken) {
    config.headers["Authorization"] = `Bearer ${adminToken}`;
  }
  return config;
});

// Normalize Pydantic v2 array errors into a plain string so toast.error() never crashes
http.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
      err.response.data.detail = detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
    }
    return Promise.reject(err);
  }
);

export const api = {
  topics: () => http.get("/topics").then((r) => r.data),
  agents: () => http.get("/agents").then((r) => r.data),
  // Returns ArrayBuffer (MP3 bytes) or null (204 = TTS unavailable)
  agentSpeak: (agent, language, text, topic = "", gender = "") =>
    http.post("/agent/speak", { agent, language, text, topic, gender }, { responseType: "arraybuffer" })
      .then((r) => (r.status === 204 ? null : r.data))
      .catch(() => null),
  agentAvatarSpeak: (agent, language, text, topic = "") =>
    http.post("/agent/avatar-speak", { agent, language, text, topic }).then((r) => r.data).catch(() => null),
  avatarStart: ({ gender, lang, topic } = {}) =>
    http.post("/avatar/start", { gender, lang, topic }).then((r) => r.data),
  config: () => http.get("/config").then((r) => r.data),

  otpSend: (data) => http.post("/otp/send", data).then((r) => r.data),
  otpVerify: (data) => http.post("/otp/verify", data).then((r) => r.data),

  blitzStart: (data) => http.post("/blitz/start", data).then((r) => r.data),
  blitzEvaluate: (data) => http.post("/blitz/evaluate", data).then((r) => r.data),
  blitzShare: (id) => http.get(`/blitz/share/${id}`).then((r) => r.data),
  blitzMcqStart: (data) => http.post("/blitz/mcq-start", data).then((r) => r.data),
  blitzMcqComplete: (data) => http.post("/blitz/mcq-complete", data).then((r) => r.data),
  blitzMyAttempts: (phone) => http.get(`/blitz/my-attempts?phone=${encodeURIComponent(phone)}`).then((r) => r.data),
  blitzLeaderboard: () => http.get("/public/blitz-leaderboard").then((r) => r.data),
  mockLeaderboard:  () => http.get("/public/mock-leaderboard").then((r) => r.data),

  chatMessage: (data) => http.post("/chat/message", data).then((r) => r.data),

  verify: (id) => http.get(`/verify/${id}`).then((r) => r.data),
  qrUrl: (id) => `${API}/verify/${id}/qr.png`,

  battleCreate: (data) => http.post("/battle/create", data).then((r) => r.data),
  battleNext: (roomId) => http.post(`/battle/${roomId}/next-round`).then((r) => r.data),
  battleScore: (roomId, data) => http.post(`/battle/${roomId}/score-round`, data).then((r) => r.data),
  battleFinish: (roomId) => http.post(`/battle/${roomId}/finish`).then((r) => r.data),
  battleGet: (roomId) => http.get(`/battle/${roomId}`).then((r) => r.data),
  battleClaim: (roomId, data) => http.post(`/battle/${roomId}/claim`, data).then((r) => r.data),

  userLookup: (phone) => http.get(`/user/lookup`, { params: { phone } }).then((r) => r.data),
  userQuickLogin: (otp) => http.post(`/user/quick-login`, { otp }).then((r) => r.data),
  userTournamentStatus: (phone) => http.get(`/user/tournament-status`, { params: { phone } }).then((r) => r.data),
  userVerifyBattleEntry: (phone, entry_code) => http.post(`/user/verify-battle-entry`, { phone, entry_code }).then((r) => r.data),
  userResendBattleEntry: (phone) => http.post(`/user/resend-battle-entry`, { phone }).then((r) => r.data),
  battleJoin: (data) => http.post(`/battle/join`, data).then((r) => r.data),
  battleQueueStatus: (joinId) => http.get(`/battle/queue/${joinId}`).then((r) => r.data),
  battleQueueLeave: (joinId) => http.post(`/battle/queue/leave`, { join_id: joinId }).then((r) => r.data),
  battleSetTopic: (roomId, topic) => http.post(`/battle/${roomId}/set-topic`, { topic }).then((r) => r.data),
  battleSetPlayerTopics: (roomId, p1_topic, p2_topic, lang = "en", agent_gender = "male", p1_lang = "en", p2_lang = "en") =>
    http.post(`/battle/${roomId}/set-player-topics`, { p1_topic, p2_topic, lang, agent_gender, p1_lang, p2_lang }).then((r) => r.data),
  qrJoinTheBattle: () => `${API}/qr/jointhebattle.png`,

  // --- event access code ---
  eventCodeCurrent: () => http.get("/event-code/current").then((r) => r.data),
  eventCodeVerify: (code) => http.post("/event-code/verify", { code }).then((r) => r.data),
  adminGenerateEventCode: () => http.post("/admin/event-code/generate").then((r) => r.data),
  adminGetEventCode: () => http.get("/admin/event-code").then((r) => r.data),

  // --- player stats ---
  adminPlayerStats: () => http.get("/admin/player-stats").then((r) => r.data),
  adminBattleChallenges: () => http.get("/admin/battle-challenges").then((r) => r.data),

  // --- admin auth ---
  adminSendOtp: (email) => http.post("/admin/auth/send-otp", { email }).then((r) => r.data),
  adminVerifyOtp: (email, otp) => http.post("/admin/auth/verify", { email, otp }).then((r) => r.data),
  adminMe: () => http.get("/admin/auth/me").then((r) => r.data),
  adminLogout: () => http.post("/admin/auth/logout").then((r) => r.data),
  adminRegisteredUsers: () => http.get("/admin/registered-users").then((r) => r.data),
  adminNextTournamentName: () => http.get("/admin/next-tournament-name").then((r) => r.data),
  tournamentRename: (id, name) => http.patch(`/tournament/${id}/rename`, { name }).then((r) => r.data),

  // --- tournaments ---
  tournamentCreate: (data) => http.post(`/tournament/create`, data).then((r) => r.data),
  tournamentList: () => http.get(`/tournaments`).then((r) => r.data),
  tournamentCurrent: () => http.get(`/tournament/current`).then((r) => r.data),
  tournamentGet: (id) => http.get(`/tournament/${id}`).then((r) => r.data),
  tournamentStartMatch: (id, level, match_num) => http.post(`/tournament/${id}/start-match`, { level, match_num }).then((r) => r.data),
  tournamentSeedFromRegistered: (id, count = 0) => http.post(`/tournament/${id}/seed-from-registered?count=${count}`).then((r) => r.data),
  tournamentGoLive: (id) => http.post(`/tournament/${id}/go-live`).then((r) => r.data),
  adminAutoProvision: () => http.post(`/admin/auto-provision-tournaments`).then((r) => r.data),

  // --- tournament self-registration (admin-controlled battle) ---
  tournamentSelfRegister: (data) => http.post("/tournament/self-register", data).then((r) => r.data),
  tournamentMyMatchStatus: (phone) => http.get("/tournament/my-match-status", { params: { phone } }).then((r) => r.data),
  adminBattleRegistrations: () => http.get("/admin/battle-registrations").then((r) => r.data),
  adminStopAllMatches: () => http.post("/admin/stop-all-matches").then((r) => r.data),
  adminResetDb: () => http.post("/admin/reset-db").then((r) => r.data),
  adminTournamentRename: (id, name) => http.patch(`/tournament/${id}/rename`, { name }).then((r) => r.data),
  adminAddPlayerToTournament: (tournamentId, phone) =>
    http.post(`/admin/tournament/${tournamentId}/add-player`, { phone }).then((r) => r.data),
  adminAssignMatch: (tournamentId, match_num, p1_phone, p2_phone) =>
    http.post(`/admin/tournament/${tournamentId}/assign-match`, { match_num, p1_phone, p2_phone }).then((r) => r.data),
  adminGenerateQuickBattleOtp: () =>
    http.post("/admin/generate-quick-battle-otp").then((r) => r.data),
  battleQuickStart: (phone1, phone2, admin_otp) =>
    http.post("/battle/quick-start", { phone1, phone2, admin_otp }).then((r) => r.data),

  // --- public pages ---
  publicLiveMatches: () => http.get("/public/live-matches").then((r) => r.data),
  publicLeaderboard: () => http.get("/public/leaderboard").then((r) => r.data),
  publicMatchDetail: (roomId) => http.get(`/public/match/${roomId}`).then((r) => r.data),

  // --- mock interview ---
  mockPublicPositions: () => http.get("/mock/positions/public").then((r) => r.data),
  mockSendOtp: (email, name) => http.post("/mock/auth/send-otp", { email, name }).then((r) => r.data),
  mockVerifyOtp: (email, otp) => http.post("/mock/auth/verify-otp", { email, otp }).then((r) => r.data),
  mockMe: () => http.get("/mock/auth/me").then((r) => r.data),
  mockApprovalStatus: (email) => http.get(`/mock/auth/approval-status?email=${encodeURIComponent(email)}`).then((r) => r.data),
  mockPositions: () => http.get("/mock/positions").then((r) => r.data),
  mockPosition: (id) => http.get(`/mock/positions/${id}`).then((r) => r.data),
  mockSessionStart: (positionId) => http.post("/mock/session/start", { position_id: positionId }).then((r) => r.data),
  mockAvatarStart: (sessionId) => http.post(`/mock/session/${sessionId}/avatar-start`).then((r) => r.data),
  mockSessionMessage: (sessionId, message, hint_requested = false) =>
    http.post(`/mock/session/${sessionId}/message`, { message, hint_requested }).then((r) => r.data),
  mockSessionUploadResume: (sessionId, formData) => {
    const token = localStorage.getItem("mock_token");
    return http.post(`/mock/session/${sessionId}/upload-resume`, formData, {
      headers: { "Content-Type": "multipart/form-data", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }).then((r) => r.data);
  },
  mockSessionCodeSubmit: (sessionId, code, language) =>
    http.post(`/mock/session/${sessionId}/code-submit`, { code, language }).then((r) => r.data),
  mockSessionUploadRecording: (sessionId, blob) => {
    const token = localStorage.getItem("mock_token");
    const form = new FormData();
    form.append("file", blob, "recording.webm");
    return http.post(`/mock/session/${sessionId}/recording`, form, {
      headers: { "Content-Type": "multipart/form-data", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }).then((r) => r.data);
  },
  mockSessionEnd: (sessionId) => http.post(`/mock/session/${sessionId}/end`).then((r) => r.data),
  mockSessionGet: (sessionId) => http.get(`/mock/session/${sessionId}`).then((r) => r.data),
  mockSessionFeedback: (sessionId) => http.get(`/mock/session/${sessionId}/feedback`).then((r) => r.data),
  mockMySessions: () => http.get("/mock/my-sessions").then((r) => r.data),
  mockSessionRecordingUrl: (id) => {
    // Stream via our backend so presigned-URL region/CORS issues are bypassed entirely.
    // The backend accepts either token type on the streaming endpoint.
    const token = localStorage.getItem("mock_token") || localStorage.getItem("proctorly_admin_token") || "";
    const url = `${API}/mock/session/${id}/recording-stream?token=${encodeURIComponent(token)}`;
    return Promise.resolve({ url, expires_in: 3600 });
  },

  // --- admin mock ---
  adminMockUsers: () => http.get("/admin/mock/users").then((r) => r.data),
  adminMockApproveUser: (email, approved, rejection_reason = "") =>
    http.post(`/admin/mock/users/${encodeURIComponent(email)}/approve`, { approved, rejection_reason }).then((r) => r.data),
  adminMockSessions: () => http.get("/admin/mock/sessions").then((r) => r.data),
  adminMockSessionDetail: (id) => http.get(`/admin/mock/sessions/${id}`).then((r) => r.data),
  adminMockRecordingUrl: (id) => {
    // Reuse the same backend streaming proxy; admins' token is accepted there.
    const token = localStorage.getItem("proctorly_admin_token") || localStorage.getItem("mock_token") || "";
    const url = `${API}/mock/session/${id}/recording-stream?token=${encodeURIComponent(token)}`;
    return Promise.resolve({ url, expires_in: 3600 });
  },
  adminMockForceEndSession: (id) => http.post(`/admin/mock/sessions/${id}/force-end`).then((r) => r.data),
  adminMockFixRecordingDurations: () => http.post("/admin/mock/fix-recording-durations").then((r) => r.data),
  adminMockFixRecordingDurationsStatus: () => http.get("/admin/mock/fix-recording-durations").then((r) => r.data),
  adminMockPositions: () => http.get("/admin/mock/positions").then((r) => r.data),
  adminMockCreatePosition: (body) => http.post("/admin/mock/positions", body).then((r) => r.data),
  adminMockStats: () => http.get("/admin/mock/stats").then((r) => r.data),
};
